import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import authService from '../services/authService';
import { User, LoginRequest, LoginResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';
const PROACTIVE_REFRESH_BEFORE_SECONDS = 300; // refresh 5 min before expiry

interface JwtPayload {
  sub: string;
  exp: number;
  jti: string;
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  clearForceChange: () => void;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule a proactive token refresh based on current token expiry
  const scheduleProactiveRefresh = (token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    const decoded = decodeJwtPayload(token);
    try {
      if (!decoded) return;
      const nowSeconds = Math.floor(Date.now() / 1000);
      const refreshAt = decoded.exp - PROACTIVE_REFRESH_BEFORE_SECONDS;
      const delayMs = (refreshAt - nowSeconds) * 1000;

      if (delayMs > 0) {
        refreshTimerRef.current = setTimeout(async () => {
          try {
            const response = await axios.post(
              `${API_BASE_URL}/auth/refresh`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
              }
            );
            const newToken: string = response.data.access_token;
            localStorage.setItem('access_token', newToken);
            if (response.data.user) {
              const updatedUser = response.data.user as User;
              localStorage.setItem('user', JSON.stringify(updatedUser));
              setUser(updatedUser);
              // Re-schedule for the new token
              scheduleProactiveRefresh(newToken);
            }
          } catch {
            // Proactive refresh failure is silent — the response interceptor
            // in api.ts will handle 401s on the next real request
          }
        }, delayMs);
      }
    } catch {
      // Token not decodable — skip scheduling
    }
  };

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    const token = localStorage.getItem('access_token');
    if (currentUser && token) {
      setUser(currentUser);
      setMustChangePassword(currentUser.must_change_password ?? false);
      scheduleProactiveRefresh(token);
    }
    setIsLoading(false);

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await authService.login(credentials);
    setUser(response.user);
    setMustChangePassword(response.must_change_password ?? false);
    const token = localStorage.getItem('access_token');
    if (token) {
      scheduleProactiveRefresh(token);
    }
    return response;
  };

  const logout = async (): Promise<void> => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    await authService.logout();
    setUser(null);
    setMustChangePassword(false);
  };

  const clearForceChange = () => {
    setMustChangePassword(false);
    if (user) {
      const updated = { ...user, must_change_password: false };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    mustChangePassword,
    login,
    logout,
    clearForceChange,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
